import { useState } from "react";
import { ArrowRight, ArrowLeft, Loader2, Check } from "lucide-react";
import {
  trackQualifyingSubmission,
  trackNewsletterSignup,
  type QualifyingFormValues,
  type NewsletterValues,
} from "@/lib/get-found-tracking";
import { Eyebrow } from "./GetFoundSections";

const HEAR_OPTIONS = ["Instagram", "TikTok", "Google", "Referral"];

type Values = {
  hearAbout: string;
  goal: string;
  budget: string;
  firstName: string;
  lastName: string;
  email: string;
  businessName: string;
  workWithUs: string;
};

const EMPTY: Values = {
  hearAbout: "",
  goal: "",
  budget: "",
  firstName: "",
  lastName: "",
  email: "",
  businessName: "",
  workWithUs: "",
};

const inputClass =
  "w-full bg-black/40 border border-white/15 rounded-lg px-4 py-3.5 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#b85736] focus:border-[#b85736] transition-colors text-base";
const labelClass =
  "block font-mono text-[11px] uppercase tracking-[0.15em] text-white/60 font-bold mb-2.5";

type ChoiceOption = { value: string; label: string; sub?: string };

/** Ordered step ids. Branches after the budget question. */
function getStepOrder(values: Values): string[] {
  const order = ["hearAbout", "goal", "budget"];
  if (values.budget === "no") {
    order.push("newsletterCapture");
  } else if (values.budget === "yes") {
    order.push("nameEmail", "business", "workWithUs");
  }
  return order;
}

function stepTitle(id: string): string {
  switch (id) {
    case "hearAbout":
      return "How did you hear about the Get Found Playbook?";
    case "goal":
      return "What's your goal with the guide?";
    case "budget":
      return "This playbook costs $47. Is that within your budget?";
    case "newsletterCapture":
      return "No problem — let's keep in touch.";
    case "nameEmail":
      return "Great. What's your name and email?";
    case "business":
      return "What's your business called?";
    case "workWithUs":
      return "Would you like us to reach out about working 1:1?";
    default:
      return "";
  }
}

function stepHint(id: string): string | undefined {
  switch (id) {
    case "hearAbout":
      return "Just curious where folks are finding us.";
    case "goal":
      return "We'll tailor what we send you.";
    case "budget":
      return "Be honest — there's a path either way.";
    case "newsletterCapture":
      return "Drop your name and email and we'll add you to the newsletter.";
    case "nameEmail":
      return "Your audit report and access code get sent here.";
    case "business":
      return "This goes into every prompt and your audit report.";
    case "workWithUs":
      return "Our done-for-you prices start at $1500/mo.";
    default:
      return undefined;
  }
}

function stepValid(id: string, values: Values): boolean {
  const v = (s: string) => String(s ?? "").trim() !== "";
  switch (id) {
    case "hearAbout":
      return v(values.hearAbout);
    case "goal":
      return v(values.goal);
    case "budget":
      return values.budget === "yes" || values.budget === "no";
    case "newsletterCapture":
      return v(values.firstName) && v(values.lastName) && v(values.email);
    case "nameEmail":
      return v(values.firstName) && v(values.lastName) && v(values.email);
    case "business":
      return v(values.businessName);
    case "workWithUs":
      return values.workWithUs === "yes" || values.workWithUs === "no";
    default:
      return true;
  }
}

function ChoiceButtons({
  options,
  value,
  onSelect,
}: {
  options: ChoiceOption[];
  value: string;
  onSelect: (v: string) => void;
}) {
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onSelect(o.value)}
            className={`text-left rounded-xl border p-5 transition-all duration-150 ${
              active
                ? "border-[#b85736] bg-[#b85736]/15 ring-2 ring-[#b85736]/40"
                : "border-white/15 bg-black/30 hover:border-white/30 hover:bg-black/50"
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="font-heading font-extrabold text-lg uppercase tracking-tight text-white">
                {o.label}
              </span>
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-full border transition-colors ${
                  active
                    ? "border-[#b85736] bg-[#b85736] text-[#151515]"
                    : "border-white/25 text-transparent"
                }`}
              >
                <Check className="h-4 w-4" />
              </span>
            </div>
            {o.sub && (
              <p className="mt-1.5 text-white/55 text-sm leading-relaxed">
                {o.sub}
              </p>
            )}
          </button>
        );
      })}
    </div>
  );
}

function StepBody({
  id,
  values,
  setValue,
}: {
  id: string;
  values: Values;
  setValue: (name: keyof Values, v: string) => void;
}) {
  if (id === "hearAbout") {
    return (
      <ChoiceButtons
        options={HEAR_OPTIONS.map((h) => ({
          value: h.toLowerCase(),
          label: h,
        }))}
        value={values.hearAbout}
        onSelect={(v) => setValue("hearAbout", v)}
      />
    );
  }

  if (id === "budget") {
    return (
      <ChoiceButtons
        options={[
          { value: "yes", label: "Yes", sub: "Continue to the full form." },
          {
            value: "no",
            label: "Not right now",
            sub: "We'll save your email for the newsletter instead.",
          },
        ]}
        value={values.budget}
        onSelect={(v) => setValue("budget", v)}
      />
    );
  }

  if (id === "workWithUs") {
    return (
      <ChoiceButtons
        options={[
          {
            value: "yes",
            label: "Yes, let's talk",
            sub: "We'll reach out about a done-for-you plan.",
          },
          {
            value: "no",
            label: "Not right now",
            sub: "Just the playbook, thanks.",
          },
        ]}
        value={values.workWithUs}
        onSelect={(v) => setValue("workWithUs", v)}
      />
    );
  }

  if (id === "goal") {
    return (
      <div>
        <label className={labelClass} htmlFor="goal">
          Your goal *
        </label>
        <textarea
          id="goal"
          name="goal"
          rows={3}
          required
          placeholder="e.g. Rank #1 on Google Maps for my service in my city"
          value={values.goal}
          onChange={(e) => setValue("goal", e.target.value)}
          className={inputClass}
        />
      </div>
    );
  }

  // Name + email steps (newsletterCapture and nameEmail share these fields)
  if (id === "newsletterCapture" || id === "nameEmail") {
    return (
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-1">
          <label className={labelClass} htmlFor="firstName">
            First name *
          </label>
          <input
            id="firstName"
            name="firstName"
            type="text"
            required
            autoComplete="given-name"
            value={values.firstName}
            onChange={(e) => setValue("firstName", e.target.value)}
            className={inputClass}
          />
        </div>
        <div className="sm:col-span-1">
          <label className={labelClass} htmlFor="lastName">
            Last name *
          </label>
          <input
            id="lastName"
            name="lastName"
            type="text"
            required
            autoComplete="family-name"
            value={values.lastName}
            onChange={(e) => setValue("lastName", e.target.value)}
            className={inputClass}
          />
        </div>
        <div className="sm:col-span-2">
          <label className={labelClass} htmlFor="email">
            Email *
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            autoComplete="email"
            value={values.email}
            onChange={(e) => setValue("email", e.target.value)}
            className={inputClass}
          />
        </div>
      </div>
    );
  }

  if (id === "business") {
    return (
      <div>
        <label className={labelClass} htmlFor="businessName">
          Business name *
        </label>
        <input
          id="businessName"
          name="businessName"
          type="text"
          required
          placeholder="e.g. Acme Roofing"
          value={values.businessName}
          onChange={(e) => setValue("businessName", e.target.value)}
          className={inputClass}
        />
      </div>
    );
  }

  return null;
}

export function QualifyingFormSection() {
  const [values, setValues] = useState<Values>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<null | "checkout" | "newsletter">(null);
  const [direction, setDirection] = useState<"forward" | "back">("forward");
  const [stepIndex, setStepIndex] = useState(0);

  const order = getStepOrder(values);
  const currentId = order[stepIndex] ?? "hearAbout";
  const total = order.length;
  const progress = Math.round((stepIndex / Math.max(total, 1)) * 100);

  const setValue = (name: keyof Values, v: string) =>
    setValues((prev) => ({ ...prev, [name]: v }));

  const canAdvance = stepValid(currentId, values);

  const goNext = () => {
    if (!canAdvance) return;
    if (stepIndex < total - 1) {
      setDirection("forward");
      setStepIndex((s) => s + 1);
    } else {
      submit();
    }
  };

  const goBack = () => {
    if (stepIndex > 0) {
      setDirection("back");
      setStepIndex((s) => s - 1);
    }
  };

  const submit = () => {
    setSubmitting(true);
    if (values.budget === "no") {
      const newsletter: NewsletterValues = {
        hearAbout: values.hearAbout,
        goal: values.goal,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
      };
      try {
        trackNewsletterSignup(newsletter);
      } catch {
        /* fire-and-forget */
      }
      setDone("newsletter");
      setSubmitting(false);
      return;
    }

    const full: QualifyingFormValues = {
      hearAbout: values.hearAbout,
      goal: values.goal,
      budget: values.budget,
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      businessName: values.businessName,
      workWithUs: values.workWithUs,
    };
    try {
      trackQualifyingSubmission(full);
    } catch {
      /* fire-and-forget */
    }
    setDone("checkout");
    setSubmitting(false);
    setTimeout(() => {
      window.location.assign("/get-found/checkout");
    }, 900);
  };

  if (done === "checkout") {
    return (
      <section
        id="qualify"
        className="bg-[#111118] border-y border-white/10 px-5 md:px-8 py-16 md:py-24 scroll-mt-24"
      >
        <div className="max-w-xl mx-auto flex flex-col items-center gap-4 text-center py-12">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#b85736]/15 border border-[#b85736]/40">
            <Check className="h-8 w-8 text-[#b85736]" />
          </div>
          <h3 className="font-heading font-extrabold text-2xl uppercase tracking-tight text-white">
            Taking you to checkout…
          </h3>
          <Loader2 className="h-6 w-6 animate-spin text-[#b85736]" />
        </div>
      </section>
    );
  }

  if (done === "newsletter") {
    return (
      <section
        id="qualify"
        className="bg-[#111118] border-y border-white/10 px-5 md:px-8 py-16 md:py-24 scroll-mt-24"
      >
        <div className="max-w-xl mx-auto flex flex-col items-center gap-4 text-center py-12">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#b85736]/15 border border-[#b85736]/40">
            <Check className="h-8 w-8 text-[#b85736]" />
          </div>
          <h3 className="font-heading font-extrabold text-2xl uppercase tracking-tight text-white">
            You're on the list.
          </h3>
          <p className="text-white/60 text-sm md:text-base max-w-sm">
            We saved your email for the newsletter. When you're ready to get
            found, the playbook will be here.
          </p>
          <a
            href="/get-found"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-[#b85736] hover:text-white transition-colors"
          >
            Back to the top <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    );
  }

  return (
    <section
      id="qualify"
      className="bg-[#111118] border-y border-white/10 px-5 md:px-8 py-16 md:py-24 scroll-mt-24"
    >
      <div className="max-w-xl mx-auto">
        <div className="text-center space-y-3 mb-8">
          <Eyebrow>One Quick Step</Eyebrow>
          <h2 className="font-heading text-2xl md:text-3xl font-extrabold uppercase tracking-tight text-white">
            Tell us about your business
          </h2>
          <p className="text-white/60 text-sm md:text-base">
            A few quick questions so we can tailor your audit and prompts. Then
            you'll go straight to secure checkout.
          </p>
        </div>

        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-white/50 font-bold">
              Step {stepIndex + 1} of {total}
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#b85736] font-bold">
              {progress}%
            </span>
          </div>
          <div className="h-1.5 w-full bg-black/50 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#b85736] rounded-full transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div
          key={currentId}
          className="bg-black/30 border border-white/10 rounded-xl p-6 md:p-8"
          style={{
            animation: `${direction === "forward" ? "gfSlideIn" : "gfSlideInBack"} 0.28s ease-out`,
          }}
        >
          <h3 className="font-heading font-extrabold text-xl md:text-2xl text-white mb-1.5">
            {stepTitle(currentId)}
          </h3>
          {stepHint(currentId) && (
            <p className="text-white/50 text-sm mb-6">{stepHint(currentId)}</p>
          )}

          <StepBody id={currentId} values={values} setValue={setValue} />

          {/* Navigation */}
          <div className="mt-8 flex items-center justify-between gap-3">
            {stepIndex > 0 ? (
              <button
                type="button"
                onClick={goBack}
                className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-white/60 hover:text-white transition-colors px-3 py-2"
              >
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
            ) : (
              <span />
            )}

            <button
              type="button"
              onClick={goNext}
              disabled={!canAdvance || submitting}
              className="inline-flex items-center justify-center gap-2.5 font-bold text-base px-7 py-3.5 rounded-xl transition-all duration-150 text-[#151515] bg-[#b85736] hover:bg-[#eee9e3] uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ boxShadow: "0 0 30px rgba(218, 111, 77, 0.3)" }}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" /> Saving…
                </>
              ) : stepIndex === total - 1 ? (
                <>
                  Continue to checkout <ArrowRight className="h-5 w-5" />
                </>
              ) : (
                <>
                  Continue <ArrowRight className="h-5 w-5" />
                </>
              )}
            </button>
          </div>
        </div>

        <p className="mt-6 font-mono text-xs text-white/40 text-center">
          Your answers personalize your audit. We'll never sell your info.
        </p>
      </div>

      <style>{`
        @keyframes gfSlideIn {
          from { opacity: 0; transform: translateX(24px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes gfSlideInBack {
          from { opacity: 0; transform: translateX(-24px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </section>
  );
}
